import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { FirebaseError } from 'firebase/app';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';

import { axiosInstance } from '../../axios';
import { db } from '../../config';

export const UNSUBSCRIBES_QUERY_KEY = ['unsubscribes'] as const;

export interface UnsubscribeRecord {
  email: string;
  /** Skąd przyszło wypisanie: link w mailu albo ręcznie z panelu. */
  source: string;
  createdAt: number | null;
}

const toMillis = (value: unknown): number | null => {
  const stamp = value as { toMillis?: () => number } | undefined;
  return typeof stamp?.toMillis === 'function' ? stamp.toMillis() : null;
};

/** Adresy, do których nie wolno wysyłać. Zapisuje je backend, panel tylko czyta. */
export const useGetUnsubscribes = (): UseQueryResult<UnsubscribeRecord[], FirebaseError> => useQuery<
  UnsubscribeRecord[],
  FirebaseError
>({
  queryKey: UNSUBSCRIBES_QUERY_KEY,
  queryFn: async () => {
    const snapshot = await getDocs(query(collection(db, 'unsubscribes'), orderBy('createdAt', 'desc')));
    return snapshot.docs.map((entry) => {
      const data = entry.data();
      return {
        email: typeof data.email === 'string' ? data.email : entry.id,
        source: typeof data.source === 'string' ? data.source : 'link',
        createdAt: toMillis(data.createdAt),
      };
    });
  },
  refetchOnWindowFocus: false,
});

/** Cofnięcie wypisania. Idzie przez backend, bo klient nie ma prawa zapisu do kolekcji. */
export const useResubscribeEmails = (): UseMutationResult<void, AxiosError, { emails: string[] }> => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, { emails: string[] }>({
    mutationFn: async ({ emails }) => {
      await axiosInstance.post('/v3-resubscribeEmail', { emails });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: UNSUBSCRIBES_QUERY_KEY });
    },
  });
};
